import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Calculator, DollarSign, Users, TrendingUp } from 'lucide-react';

const CostPerLeadCalculator = () => {
  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/cost-per-lead-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;
  const [totalCost, setTotalCost] = useState(5000);
  const [totalLeads, setTotalLeads] = useState(200);
  const [costPerLead, setCostPerLead] = useState(0);

  useEffect(() => {
    calculateCostPerLead();
  }, [totalCost, totalLeads]);

  const calculateCostPerLead = () => {
    const calculatedCostPerLead = totalLeads > 0 ? totalCost / totalLeads : 0;
    setCostPerLead(parseFloat(calculatedCostPerLead.toFixed(2)));
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Cost Per Lead Calculator: Free CPL Calculator Tool</title>
        <meta name="description" content="Calculate your Cost Per Lead (CPL) instantly with our free calculator. Optimize your lead generation campaigns and marketing spend effectively." />
        <meta name="keywords" content="CPL calculator, cost per lead calculator, lead generation metrics, marketing ROI, digital marketing tools" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free CPL Calculator: Measure Your Cost Per Lead" />
        <meta property="og:description" content="Calculate your Cost Per Lead instantly. Optimize your lead generation campaigns with our free CPL calculator." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free CPL Calculator: Measure Your Cost Per Lead" />
        <meta property="twitter:description" content="Calculate your Cost Per Lead instantly. Optimize your lead generation campaigns with our free CPL calculator." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Cost Per Lead Calculator",
            "description": "Free calculator to measure Cost Per Lead for marketing campaigns",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate Cost Per Lead",
              "Marketing cost tracking",
              "Lead tracking",
              "CPL optimization tips"
            ]
          })}
        </script>
      </Helmet>
      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Cost Per Lead Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Cost</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={totalCost}
                  onChange={(e) => setTotalCost(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="5000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Leads</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Users className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={totalLeads}
                  onChange={(e) => setTotalLeads(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="200"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            ${costPerLead}
            <span className="text-xl text-gray-500 ml-2">per lead</span>
            <TrendingUp className="w-8 h-8 ml-2 text-indigo-400" />
          </div>
          <p className="text-gray-600">Cost Per Lead</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation</h2>
        <p className="text-gray-600">
          Your Cost Per Lead is ${costPerLead}. This means you're spending an average of ${costPerLead} to acquire each new lead. A lower Cost Per Lead generally indicates a more efficient lead generation campaign, but it's important to consider the quality of leads and their potential to convert into customers.
        </p>
        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Tips to Reduce Cost Per Lead</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Improve your targeting to reach more qualified prospects</li>
          <li>Optimize your landing pages and lead capture forms</li>
          <li>Test different ad creatives and messaging</li>
          <li>Use retargeting to re-engage interested prospects</li>
          <li>Implement lead scoring to focus on high-quality leads</li>
          <li>Explore different marketing channels to find the most cost-effective ones</li>
        </ul>
      </div>
    </div>
  );
};

export default CostPerLeadCalculator;
